/* eslint-disable no-param-reassign */
import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import { createValidity } from '@mhub/web-validations';
import Helper from './Helper';

class Validate extends Component {
  static propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    message: PropTypes.string,
    children: PropTypes.node,
    validation: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
          validate: PropTypes.func,
          option: PropTypes.object,
        }),
      ]).isRequired,
    ),
    onChange: PropTypes.func,
    validateLabel: PropTypes.string,
    onError: PropTypes.func,
    hideMessage: PropTypes.bool,
    showThousandSeparator: PropTypes.bool,
    style: PropTypes.oneOfType([
      PropTypes.object,
    ]),
    t: PropTypes.func,
  }
  constructor(props) {
    super(props);
    this.state = {
      isValid: true,
      message: null,
    };
  }
  getLabels() {
    const labels = Children
      .map(this.props.children, (child) => {
        if (!child) return null;
        return child.props.label;
      })
      .filter(Boolean);
    return labels.join(', ');
  }
  getNames() {
    const names = Children
      .map(this.props.children, (child) => {
        if (!child) return null;
        return child.props.name;
      })
      .filter(Boolean);
    return names.join(', ');
  }
  getTypes() {
    const types = Children
      .map(this.props.children, (child) => {
        if (!child) return null;
        return this.props.showThousandSeparator ? 'number' : child.props.type;
      })
      .filter(Boolean);
    return types.join(', ');
  }
  translateValidationMessage = (message) => {
    const { t } = this.props;
    if (!t || !message) return message;

    if (message.includes('is required')) {
      const fieldName = message.replace(' is required', '').trim();
      const translatedRequired = t('is required', { defaultValue: 'is required' });
      return `${fieldName} ${translatedRequired}`;
    } else if (message.includes('is not a valid mobile number')) {
      const fieldName = message.replace('is not a valid mobile number', '').trim();
      const translatedInvalidPhone = t('is not a valid mobile number', { defaultValue: 'is not a valid mobile number' });
      return `${fieldName} ${translatedInvalidPhone}`;
    } else if (message.includes('does not match the format')) {
      const fieldName = message.replace(' does not match the format', '').trim();
      const translatedDoesNotMatch = t('does not match the format', { defaultValue: 'does not match the format' });
      return `${fieldName} ${translatedDoesNotMatch}`;
    } else if (message.match(/needs to be at least \d+/)) {
      const match = message.match(/needs to be at least (\d+)/);
      const minValue = match ? match[1] : '0';
      const fieldName = message.replace(/needs to be at least \d+/, '').trim();
      const translatedNeedsToBeAtLeast = t('needs to be at least {{number}}', { number: minValue, defaultValue: `needs to be at least ${minValue}` });
      return `${fieldName} ${translatedNeedsToBeAtLeast}`;
    } else if (message.includes('is not a valid email address')) {
      const fieldName = message.replace('is not a valid email address', '').trim();
      const translatedInvalidEmail = t('is not a valid email address', { defaultValue: 'is not a valid email address' });
      return `${fieldName} ${translatedInvalidEmail}`;
    }
    return message;
  }
  handleOnChange = (e, data) => {
    // bubble up onChange
    if (this.props.onChange) {
      this.props.onChange(e, data);
    }
    if (data && data.value) {
      this.validate(data.value);
    } else {
      this.validate(e.target.value);
    }
  }
  validate = (value) => {
    const label = (this.props.validateLabel || this.props.label) || this.getLabels();
    const name = this.props.name || this.getNames();
    const type = this.getTypes();
    const message = this.props.message;
    return new Promise((res) => {
      if (this.props.validation && this.props.validation.length > 0) {
        if (type === 'number') {
          // If value is empty string, will become NaN
          value = parseFloat(value);
          if (isNaN(value)) {
            value = 0;
          }
        }
        let validate;
        this.props.validation.every((v) => {
          if (v && v.toString() && v.toString().includes('is required')) {
            // array & undefined required fix
            if ((type !== 'number') && (value === undefined || !value)) value = '';
            if (Array.isArray(value)) value = value.join();
          }
          if (typeof v === 'object') {
            validate = v.validate(value, Object.assign({ key: name, label, message }, v.option));
          } else {
            validate = v(value, { key: name, label, message });
          }
          return validate.isValid;
        });
        const translatedMessage = this.translateValidationMessage(validate.message);
        this.setState({
          isValid: validate.isValid,
          message: translatedMessage,
        }, () => {
          if (this.props.onError) {
            this.props.onError(!validate.isValid, translatedMessage);
          }
          // Return validation object with translated message
          res({ ...validate, message: translatedMessage });
        });
      } else {
        res(createValidity(name));
      }
    });
  }

  renderChild() {
    return Children.map(this.props.children, (child) => {
      // Skip null/undefined children
      if (!child) return null;

      if (this.props.hideMessage || this.props.onError) {
        return React.cloneElement(child, {
          onChange: this.handleOnChange,
        });
      }
      return React.cloneElement(child, {
        onChange: this.handleOnChange,
        error: !this.state.isValid,
      });
    });
  }
  render() {
    return (
      <div className="validate field" style={this.props.style && this.props.style}>
        {this.renderChild()}
        {
          this.state.message && !this.props.hideMessage ?
            <Helper type="error">{this.state.message}</Helper> :
            null
        }
      </div>
    );
  }
}

// withRef forwards `ref` natively to the Validate class instance, so refs
// re-attach on every render exactly like the pre-HOC `export default class`.
export default withTranslation(undefined, { withRef: true })(Validate);



// WEBPACK FOOTER //
// ./src/components/AppForm/Validate.jsx